import React from 'react';
import { Shield, Clock, Award, BadgePercent, Sparkles, Check, Save } from 'lucide-react';
import { useAlert } from '../../../../context/AlertContext';

const GeneralRules = ({ t, generalDraft, setGeneralDraft, handleSaveGeneral, isSavingGeneral, tenantData }) => {
    const { showConfirm } = useAlert();

    const updateRule = (field, value) => {
        setGeneralDraft({
            ...generalDraft,
            loyaltyRules: { ...generalDraft.loyaltyRules, [field]: value }
        });
    };

    const handleSaveWithConfirm = async () => {
        const confirmed = await showConfirm(
            'Bu değişiklik tüm müşterilerin puanlama kurallarını etkileyebilir.',
            { title: 'Genel Kuralları Kaydet', confirmText: 'Kaydet', type: 'warning' }
        );
        if (confirmed) {
            handleSaveGeneral();
        } else {
            const original = tenantData?.loyalty_rules || tenantData?.loyaltyRules || {};
            setGeneralDraft({ loyaltyRules: JSON.parse(JSON.stringify(original)) });
        }
    };

    return (
    <section className="card glass">
        <div className="settings-header-actions-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem', flexWrap: 'wrap', gap: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ padding: '8px', borderRadius: '12px', background: 'var(--primary-light)', color: 'var(--primary)' }}>
                    <Shield size={24} />
                </div>
                <div>
                    <h2 style={{ fontSize: '1.25rem', fontWeight: 700 }}>Genel Kurallar ve Puanlama</h2>
                    <p style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Müşterilerin nasıl puan kazanacağını ve statü kurallarını belirleyin.</p>
                </div>
            </div>
            <div className="settings-header-actions" style={{ display: 'flex', gap: '8px' }}>
                <button 
                    onClick={() => {
                        setGeneralDraft({
                            loyaltyRules: JSON.parse(JSON.stringify(tenantData.loyalty_rules || tenantData.loyaltyRules || {}))
                        });
                    }}
                    style={{ padding: '8px 16px', borderRadius: '12px', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-main)', fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer' }}
                >
                    Vazgeç
                </button>
                <button 
                    onClick={handleSaveWithConfirm}
                    disabled={isSavingGeneral}
                    className="btn-primary"
                    style={{ padding: '10px 24px', borderRadius: '14px', border: 'none', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 700 }}
                >
                    <Save size={18} />
                    {isSavingGeneral ? t('common.saving') : t('common.save')}
                </button>
            </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))', gap: '1.5rem', marginBottom: '2rem' }}>




            <div className="card" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div>
                        <h3 style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem' }}>{t('settings.welcomePoints')}</h3>
                        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{t('settings.welcomePointsDesc')}</p>
                    </div>
                    <Sparkles size={20} style={{ color: 'var(--text-muted)', opacity: 0.7 }} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: 'auto' }}>
                    <input
                        type="number"
                        value={generalDraft.loyaltyRules?.welcomePoints ?? 150}
                        onChange={e => updateRule('welcomePoints', parseInt(e.target.value) || 0)}
                        style={{ width: '100px', fontSize: '1.1rem', fontWeight: 600, padding: '0.75rem' }}
                    />
                    <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{t('settings.points')}</span>
                </div>
            </div>
        </div>

        <div style={{ height: '1px', background: 'var(--border)', margin: '2rem 0', opacity: 0.5 }}></div>

        <div className="card" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', padding: '0', overflow: 'hidden' }}>
            <div style={{ padding: '1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{ padding: '8px', borderRadius: '8px', background: 'rgba(var(--primary-rgb), 0.1)', color: 'var(--text-main)' }}>
                        <BadgePercent size={20} />
                    </div>
                    <div>
                        <h3 style={{ fontSize: '1rem', fontWeight: 700 }}>Harcama Bazlı Puan Sistemi</h3>
                        <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)' }}>Harcama tutarına göre otomatik puan kazanımı.</p>
                    </div>
                </div>
                <label className="switch">
                    <input
                        type="checkbox"
                        checked={generalDraft.loyaltyRules?.purchasePointsEnabled ?? true}
                        onChange={e => updateRule('purchasePointsEnabled', e.target.checked)}
                    />
                    <span className="slider"></span>
                </label>
            </div>

            {(generalDraft.loyaltyRules?.purchasePointsEnabled ?? true) && (
                <div style={{ padding: '2rem', background: 'rgba(var(--primary-rgb), 0.02)' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                        <div>
                            <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 700, marginBottom: '0.875rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Puan Kazanım Oranı
                            </label>
                            <div className="purchase-multiplier-box" style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', background: 'var(--bg-main)', border: '1px solid var(--border)', borderRadius: '16px', padding: '1rem 1.5rem', gap: '1rem', boxShadow: 'var(--shadow-sm)', width: 'fit-content', maxWidth: '100%' }}>
                                <span style={{ fontSize: '1.125rem', fontWeight: 600, color: 'var(--text-muted)' }}>Her 1 TL</span>
                                <span style={{ color: 'var(--border)' }}>=</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <input
                                        type="number"
                                        step="0.01"
                                        value={generalDraft.loyaltyRules?.purchasePointsMultiplier ?? 0.1}
                                        onChange={e => {
                                            const val = e.target.value;
                                            updateRule('purchasePointsMultiplier', val === '' ? 0 : parseFloat(val));
                                        }}
                                        style={{ width: '80px', textAlign: 'center', border: 'none', borderBottom: '2px solid var(--primary)', borderRadius: '0', padding: '4px', fontSize: '1.25rem', fontWeight: 800, color: 'var(--primary)', background: 'transparent' }}
                                    />
                                    <span style={{ fontSize: '1.125rem', fontWeight: 700, color: 'var(--primary)' }}>Puan</span>
                                </div>
                            </div>
                        </div>

                        <div style={{ display: 'flex', gap: '12px', padding: '16px', background: 'var(--bg-main)', borderRadius: '16px', border: '1px solid var(--border)', maxWidth: '100%' }}>
                            <Sparkles size={20} className="text-amber-500" style={{ flexShrink: 0, marginTop: '2px' }} />
                            <div style={{ fontSize: '0.875rem', lineHeight: '1.5' }}>
                                <span style={{ fontWeight: 700, display: 'block', marginBottom: '4px' }}>Nasıl Çalışır?</span>
                                <span style={{ color: 'var(--text-muted)' }}>Örneğin, <b>100 TL</b>'lik bir harcama yapan müşteriniz, belirlediğiniz oranla <b>{Math.floor(100 * (generalDraft.loyaltyRules?.purchasePointsMultiplier ?? 0.1))} Puan</b> kazanacaktır.</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
        <style>{`
            @media (max-width: 600px) {
                .card input[type="number"] { width: 100% !important; }
                .purchase-multiplier-box { width: 100% !important; flex-direction: column !important; align-items: flex-start !important; }
                .settings-header-actions { width: 100% !important; flex-direction: row !important; justify-content: flex-start !important; }
                .settings-header-actions button { flex: 1; align-items: center; justify-content: center; }
            }
        `}</style>
    </section>
    );
};

export default GeneralRules;